import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { StyledDatePickerContainerSignup } from "../../styles/DatePicker";
import { InputIcon } from '../../styles/PersonalInfoStep';
import CalendarMonth from '@mui/icons-material/CalendarMonth';

interface DatePickerProps {
  /** The label to display for the date picker */
  label: string;
  /** The selected date */
  value?: Dayjs | string | null;
  /** Whether the date picker is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Margin right for the calendar icon */
  marginRight?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Additional class name */
  className?: string;
  /** Callback for date selection */
  onChange: (date: Dayjs | null) => void;
  /** Type of the date picker */
  type?: string;
  /** Disable future dates (dates after today) */
  disableFuture?: boolean;
  /** Disable past dates (dates before today) */
  disablePast?: boolean;
  /** Custom minimum date */
  minDate?: Dayjs | string;
  /** Custom maximum date */
  maxDate?: Dayjs | string;
  /** Custom date validation function */
  shouldDisableDate?: (date: Dayjs) => boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  onChange,
  value = null,
  disabled = false,
  error,
  marginRight,
  required = false,
  className = "",
  type,
  disableFuture = false,
  disablePast = false,
  minDate,
  maxDate,
  shouldDisableDate,
}) => {

  const handleChange = (newValue: Dayjs | null) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (!inputValue) {
      handleChange(null);
      return;
    }

    const date = dayjs(inputValue, 'DD/MM/YYYY');
    if (date.isValid()) {
      // Check if the typed date is in the past
      const today = dayjs();
      if (date.isBefore(today, 'day')) {
        // Reset to current value or null if past date is entered
        handleChange(null);
        return;
      }
      handleChange(date);
    } else {
      handleChange(null);
    }
  };

  // Calculate min and max dates based on props
  const getMinDate = (): Dayjs | undefined => {
    if (minDate) {
      return dayjs(minDate);
    }
    if (disablePast) {
      return dayjs(); // Today
    }
    // Always set minimum date to today to prevent past date selection
    return dayjs();
  };

  const getMaxDate = (): Dayjs | undefined => {
    if (maxDate) {
      return dayjs(maxDate);
    }
    if (disableFuture) {
      return dayjs(); // Today
    }
    return undefined;
  };

  // Custom date validation function
  const customShouldDisableDate = (date: Dayjs): boolean => {
    // If custom shouldDisableDate is provided, use it
    if (shouldDisableDate) {
      return shouldDisableDate(date);
    }

    const today = dayjs();
    
    // Always disable past dates
    if (date.isBefore(today, 'day')) {
      return true;
    }
    
    // Disable future dates if disableFuture is true
    if (disableFuture && date.isAfter(today, 'day')) {
      return true;
    }
    
    // Check custom min/max dates
    if (minDate && date.isBefore(dayjs(minDate), 'day')) {
      return true;
    }
    
    if (maxDate && date.isAfter(dayjs(maxDate), 'day')) {
      return true;
    }
    
    return false;
  };

  return (
    <StyledDatePickerContainerSignup className={className} $marginRight={marginRight}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDatePicker
          slots={{
            openPickerIcon: () => (
              <InputIcon>
                <CalendarMonth fontSize="medium" />
              </InputIcon>
            ),
          }}
          label={label}
          value={value ? dayjs(value) : null}
          onChange={handleChange}
          disabled={disabled}
          format="DD/MM/YYYY"
          minDate={getMinDate()}
          maxDate={getMaxDate()}
          shouldDisableDate={customShouldDisableDate}
          slotProps={{
            textField: {
              variant: 'standard',
              fullWidth: true,
              error: !!error,
              helperText: error,
              required,
              InputProps: {
                disableUnderline: true,
              },
              InputLabelProps: {
                shrink: true,
                sx: {
                  fontWeight: 600,
                  color: '#E39435',
                  fontSize: "12px",
                  '&.Mui-focused': {
                    color: '#E39435',
                  },
                },
              },
              inputProps: {
                placeholder: 'DD/MM/YYYY',
                autoComplete: 'off',
              },
              onBlur: handleBlur,
              sx: {
                mt: '8px',
                
                '& .MuiInputBase-input': {
                  color: '#1E2F23',
                  fontSize: '14px',
                  fontWeight: 600,
                  padding: '8px 0',
                },
                '& .MuiSvgIcon-root': {
                  color: '#e39435',
                  marginTop: '-4px',
                  marginRight: marginRight || '8px',
                },
                '& .MuiIconButton-root': {
                  background: 'transparent',
                  marginTop: '-24px',
                  marginRight: '-18px',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </StyledDatePickerContainerSignup>
  );
};

export default DatePicker;